import { LitElement, html, nothing, type CSSResultGroup, type PropertyValues } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { emit } from '../utils/dom.utils.js';
import styles from './styles.js';

// ── Types ────────────────────────────────────────────────────────────────────

export type DrawerPosition = 'left' | 'right' | 'top' | 'bottom';

// ══════════════════════════════════════════════════════════════════════════════
// uwc-drawer
// ══════════════════════════════════════════════════════════════════════════════

/**
 * A slide-in panel (Drawer / Sidebar) built on the native `<dialog>` API.
 * Opens via `showModal()` for automatic focus trapping and top-layer placement.
 *
 * @fires uwc-show        - After the drawer opens.
 * @fires uwc-hide        - After the drawer finishes closing.
 * @fires uwc-before-hide - Before the close animation starts (cancelable via `event.preventDefault()`).
 *
 * @slot            - Default slot for body content.
 * @slot header     - Replaces the built-in title text.
 * @slot footer     - Action buttons rendered in the footer bar.
 *
 * @csspart dialog          - The native `<dialog>` element.
 * @csspart header          - Header container.
 * @csspart title           - Title `<span>` (when no header slot is used).
 * @csspart header-actions  - Wrapper for the built-in action buttons.
 * @csspart content         - Scrollable body wrapper.
 * @csspart footer          - Footer container.
 */
export class UwcDrawer extends LitElement {
  static override styles: CSSResultGroup = [styles];

  // ── Public properties ────────────────────────────────────────────────────

  /** Controls drawer visibility. Reflect so `<uwc-drawer open>` works. */
  @property({ type: Boolean, reflect: true })
  open = false;

  /** Title text rendered in the built-in header (overridden by `slot="header"`). */
  @property()
  header = '';

  /**
   * Which edge the drawer slides in from.
   * @type {'left' | 'right' | 'top' | 'bottom'}
   * @default 'left'
   */
  @property({ reflect: true })
  position: DrawerPosition = 'left';

  /**
   * CSS size applied to the secondary dimension:
   * - width  for `left` / `right`
   * - height for `top`  / `bottom`
   * Accepts any valid CSS length, e.g. `'20rem'`, `'30vw'`, `'300px'`.
   * @default '20rem'
   */
  @property()
  size = '20rem';

  /** Dim the page behind the drawer with a backdrop. @default true */
  @property({ type: Boolean, reflect: true })
  modal = true;

  /** Show the × close button in the header when the attribute is present. @default false */
  @property({ type: Boolean })
  closable = false;

  /** Close the drawer when Escape is pressed. @default true */
  @property({ type: Boolean, attribute: 'close-on-escape' })
  closeOnEscape = true;

  /** Close the drawer when the backdrop is clicked. @default false */
  @property({ type: Boolean, attribute: 'close-on-outside-click' })
  closeOnOutsideClick = false;

  /** Expand the drawer to fill the entire viewport. @default false */
  @property({ type: Boolean, attribute: 'full-screen', reflect: true })
  fullScreen = false;

  // ── Internal state ───────────────────────────────────────────────────────

  @state() private _hasFooter = false;

  @query('dialog') private _dialog!: HTMLDialogElement;

  // ── Lifecycle ────────────────────────────────────────────────────────────

  protected override updated(changed: PropertyValues): void {
    super.updated(changed);
    if (changed.has('open')) {
      if (this.open) {
        this._doOpen();
      } else {
        this._doClose();
      }
    }
    // Live-update position/size/fullScreen if changed while open
    if ((changed.has('size') || changed.has('position') || changed.has('fullScreen')) && this._dialog?.open) {
      this._applyPosition();
    }
  }

  // ── Private helpers ──────────────────────────────────────────────────────

  private _doOpen(): void {
    const d = this._dialog;
    if (!d || d.open) return;
    d.showModal();
    this._applyPosition();
    emit(this, 'uwc-show');
    requestAnimationFrame(() => {
      const focusable = d.querySelector<HTMLElement>(
        'uwc-button:not([disabled]), uwc-inputtext, [tabindex]:not([tabindex="-1"]), button, input, select, textarea, a[href]'
      );
      focusable?.focus();
    });
  }

  /**
   * Apply inline styles for position + size, overriding the browser's UA
   * stylesheet (which sets margin:auto, max-height/max-width constraints).
   * Inline styles always win over UA styles — same approach as uwc-dialog.
   */
  private _applyPosition(): void {
    const d = this._dialog;
    if (!d) return;

    // All four edges must be set explicitly on every branch.
    // The browser UA stylesheet sets `left:0` and `top:0` on <dialog>.
    // CSS conflict resolution: `left` beats `right` in LTR, `top` beats `bottom`.
    // So the opposing edge must be 'auto' — otherwise the UA value wins and the
    // drawer appears on the wrong side (right drawer at left, bottom drawer at top).
    d.style.margin    = '0';
    d.style.maxWidth  = 'none';
    d.style.maxHeight = 'none';

    if (this.fullScreen) {
      d.style.top    = '0';
      d.style.right  = '0';
      d.style.bottom = '0';
      d.style.left   = '0';
      d.style.width  = '100vw';
      d.style.height = '100dvh';
      return;
    }

    const size = this.size || '20rem';

    d.style.top = d.style.right = d.style.bottom = d.style.left = '0';

    switch (this.position) {
      case 'left':   d.style.right  = 'auto'; d.style.width  = size;    d.style.height = '100dvh'; break;
      case 'right':  d.style.left   = 'auto'; d.style.width  = size;    d.style.height = '100dvh'; break;
      case 'top':    d.style.bottom = 'auto'; d.style.width  = '100vw'; d.style.height = size;     break;
      case 'bottom': d.style.top    = 'auto'; d.style.width  = '100vw'; d.style.height = size;     break;
    }
  }

  private _doClose(): void {
    const d = this._dialog;
    if (!d || !d.open) return;

    const beforeEvt = emit(this, 'uwc-before-hide', { cancelable: true });
    if (beforeEvt.defaultPrevented) {
      // Revert the open property without triggering another update loop
      this.open = true;
      return;
    }

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { this._finishClose(d); return; }

    d.classList.add('is-closing');
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      d.classList.remove('is-closing');
      this._finishClose(d);
    };
    d.addEventListener('animationend', (e: AnimationEvent) => {
      if (e.animationName.startsWith('uwc-drw-out')) finish();
    });
    // Safety fallback in case animationend doesn't fire (e.g. display:none)
    setTimeout(finish, 350);
  }

  private _finishClose(d: HTMLDialogElement): void {
    if (d.open) d.close();
    emit(this, 'uwc-hide');
  }

  // ── Event handlers ───────────────────────────────────────────────────────

  private _onCancel(e: Event): void {
    e.preventDefault(); // suppress native Escape handling
    if (this.closeOnEscape) this.hide();
  }

  private _onDialogClick(e: MouseEvent): void {
    if (!this.closeOnOutsideClick) return;
    // The backdrop is the dialog element itself — content clicks bubble up
    // through the content children, not through the dialog element's box.
    if ((e.target as Element) === this._dialog) this.hide();
  }

  private _onFooterSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._hasFooter = slot.assignedNodes({ flatten: true }).length > 0;
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /** Open the drawer. */
  show()   { this.open = true;  }
  /** Close the drawer. */
  hide()   { this.open = false; }
  /** Toggle the drawer open/closed. */
  toggle() { this.open = !this.open; }

  // ── Render ────────────────────────────────────────────────────────────────

  protected override render() {
    const posClass = `uwc-drw--${this.position}`;

    return html`
      <dialog
        part="dialog"
        class=${classMap({
          'uwc-drw':          true,
          [posClass]:         true,
          'uwc-drw--full':    this.fullScreen,
          'uwc-drw--no-modal':!this.modal,
        })}
        aria-labelledby="uwc-drw-title"
        aria-modal="true"
        @cancel=${this._onCancel}
        @click=${this._onDialogClick}
      >
        <!-- ── Header ─────────────────────────────────────────────────── -->
        <div part="header" class="uwc-drw__header">
          <slot name="header">
            <span id="uwc-drw-title" part="title" class="uwc-drw__title">
              ${this.header}
            </span>
          </slot>
          ${this.closable ? html`
            <uwc-button
              part="close-button"
              icon="x-lg"
              icon-only
              text
              variant="secondary"
              size="small"
              aria-label="Close"
              @uwc-click=${() => this.hide()}
            ></uwc-button>
          ` : nothing}
        </div>

        <!-- ── Content ────────────────────────────────────────────────── -->
        <div part="content" class="uwc-drw__content">
          <slot></slot>
        </div>

        <!-- ── Footer ─────────────────────────────────────────────────── -->
        <div
          part="footer"
          class=${classMap({
            'uwc-drw__footer':       true,
            'uwc-drw__footer--hide': !this._hasFooter,
          })}
        >
          <slot name="footer" @slotchange=${this._onFooterSlotChange}></slot>
        </div>
      </dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-drawer': UwcDrawer; }
}
